/**
 * CDN Configuration
 *
 * Provides utilities for accessing static assets from the Carmen CDN (Cloud Storage).
 */

/**
 * Base URL for CDN assets served from Google Cloud Storage.
 * Falls back to empty string in dev if not configured (assets served from public/).
 */
export const CDN_BASE_URL = import.meta.env.VITE_CDN_BASE_URL || '';

/**
 * Get the full URL for a CDN asset.
 *
 * @param path - Relative path to asset in the CDN bucket (e.g., "airline-logos/aa.png")
 * @returns Full URL to the asset
 *
 * @example
 * ```ts
 * const logoUrl = getCdnUrl('airline-logos/aa.png');
 * // Returns: "https://storage.googleapis.com/carmen-public/airline-logos/aa.png"
 * ```
 */
export function getCdnUrl(path: string): string {
  // Remove leading slash if present
  const cleanPath = path.startsWith('/') ? path.slice(1) : path;

  // If CDN is configured, use it; otherwise fall back to local public path
  if (CDN_BASE_URL) {
    return `${CDN_BASE_URL}/${cleanPath}`;
  }

  // Fallback for local development without CDN
  return `/${cleanPath}`;
}

/**
 * Get the URL for an airline logo by IATA code.
 *
 * @param airlineCode - IATA airline code (e.g., "AA", "DL")
 * @returns URL to the airline logo PNG
 *
 * @example
 * ```ts
 * const logoUrl = getAirlineLogoUrl('AA');
 * // Returns: "https://storage.googleapis.com/carmen-public/airline-logos/aa.png"
 * ```
 */
export function getAirlineLogoUrl(airlineCode: string): string {
  return getCdnUrl(`airline-logos/${airlineCode.toLowerCase()}.png`);
}

/**
 * Check if CDN is enabled.
 *
 * @returns true if CDN base URL is configured
 */
export function isCdnEnabled(): boolean {
  return Boolean(CDN_BASE_URL);
}

